import { Box } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ActionData } from './components/ActionBar/ActionBar';
import { ActionBar, InlineEditorActions } from './components/ActionBar/ActionBar';
import { withWindow } from '@shared/utils/withWindow';
import { InlineEditorWrapper } from './InlineEditor.styles';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { ParentMessageListener } from './components/ParentMessageListener/ParentMessageListener';
import { telemetryData } from './InlineEditor.telemetry';
import { AnimatedBorder } from './components/AnimatedBorder/AnimatedBorder';
import type { CSSObject } from '@withjoy/joykit/components/Box/Box.types';
import type { EventPageType } from '@graphql/generated-base';
import { useIsStickyElement } from '@shared/hooks/useIsStickyElement';
import { useWebsiteDesignerV3Feature } from '@shared/hooks/useWebsiteDesignerV3Feature';
import { useId } from '@shared/joykit/packages/core/common/hooks/useId';
import { useTemplatePreviewStore } from '@apps/guest/routes/GuestSite/TemplatePreviewContext';

// FONT/VISIBILITY are secondary shortcuts that have their own iconified entry
// points in the action bar; everything else is "primary" — clicking the
// wrapper fires the first match in this list. Order encodes precedence for
// the (currently hypothetical) case where a consumer passes more than one
// primary action.
type PrimaryActionKey = Exclude<InlineEditorActions, InlineEditorActions.FONT | InlineEditorActions.VISIBILITY>;
const PRIMARY_ACTION_KEYS: readonly PrimaryActionKey[] = [
  InlineEditorActions.EDIT,
  InlineEditorActions.EDIT_PHOTO,
  InlineEditorActions.EDIT_MONOGRAM,
  InlineEditorActions.EDIT_VIDEO,
  InlineEditorActions.EDIT_THEME_COLOR,
  InlineEditorActions.ADD_PHOTO,
];

// Inset from the wrapper edge for a top/bottom-aligned actionInside button,
// keeping it clear of a centered child editor (e.g. Photo/Video vs. Monogram).
const ACTION_INSIDE_EDGE_OFFSET = '12%';

type InlineEditorProps = Readonly<{
  elementLabel: string;
  actionData: ActionData;
  wrapperType?: 'actionInside' | 'actionOutside' | 'emptyChild';
  minHeight?: string;
  wrapperCSS?: CSSObject;
  containerCSS?: CSSObject;
  inset?: number | { x: number; y: number };
  componentName:
    | 'buttons'
    | 'navigation'
    | 'pagePhoto'
    | 'headerPhoto'
    | 'headerVideo'
    | 'eventDisplayName'
    | 'eventDate'
    | 'greeting'
    | 'eventLocation'
    | 'welcomePageTheme'
    | 'page'
    | 'graphicAccent'
    | 'pageNote'
    | 'couplesName';
  pageName: Maybe<EventPageType>;
  pageSlug: string;
  isEligibleForInlineEditing?: boolean;
  stickyOnScroll?: boolean;
  showIntro?: boolean;
  labelPosition?: 'top' | 'bottom';
  // For wrapperType="actionInside" only: where the action button sits inside the wrapper.
  // Defaults to 'center'. Used to keep a full-bleed editor's button clear of a centered
  // child editor (e.g. Photo/Video background vs. the centered Monogram).
  actionInsideVerticalAlign?: 'center' | 'top' | 'bottom';
}>;

export const InlineEditor: FC<InlineEditorProps> = props => {
  const {
    componentName,
    pageName,
    pageSlug,
    elementLabel,
    actionData,
    wrapperType = 'actionOutside',
    minHeight,
    children,
    wrapperCSS = {},
    containerCSS = {},
    inset = -16,
    isEligibleForInlineEditing = true,
    stickyOnScroll = false,
    showIntro = false,
    labelPosition = 'top',
    actionInsideVerticalAlign = 'center',
  } = props;

  const [isEditing, setIsEditing] = useState(false);
  const [scaleValue, setScaleValue] = useState(1);
  const [isHovered, setIsHovered] = useState(showIntro);
  const [position, setPosition] = useState<'top' | 'bottom'>('top');

  const mouseInside = useRef(false);

  const queryParamHelper = useQueryParamHelper();
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } }, false);

  const inlineEditingEnabled = useMemo(() => queryParamHelper.getValue('feature.enableInlineEditing') === 'true', [queryParamHelper]);
  const isParentInMobileResolution = useMemo(() => queryParamHelper.getValue('feature.isMobileInlineEditMode') === 'true', [queryParamHelper]);

  // Suppress inline editing while the templates gallery previews a *different* template: the admin
  // pushes that template into this (shared) iframe via the fromTemplatePreviewPayload message, so the
  // design shown isn't the applied event design — editing it would mutate the applied design or kick
  // the user out of the gallery. The preview payload (set/cleared by those existing messages, no iframe
  // URL change so no reload) is the signal; non-strict so InlineEditor is safe outside the provider. (PRNT-4443)
  const templatePreviewStore = useTemplatePreviewStore(false);
  const isPreviewingForeignTemplate = !!templatePreviewStore?.getActivePayload();

  const adminDashboardInlineEditingEnabled = inlineEditingEnabled && isEligibleForInlineEditing && !isPreviewingForeignTemplate;

  const { value: isWebsiteDesignerV3Enabled } = useWebsiteDesignerV3Feature();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const childWrapperRef = useRef<HTMLDivElement>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);

  const isPreviewing = isInIframe();

  const observerMarkId = useId(undefined, 'inline-editor-observer-mark');
  const { isSticky } = useIsStickyElement(observerMarkId, !(stickyOnScroll && !(isMobile && isParentInMobileResolution)));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const el = e.target;
      const domNode = wrapperRef.current;

      setTimeout(() => {
        //  to handle when there is another inline item as child and that is active
        const isChildItemActive = !!domNode?.querySelector('.inline-editor-wrapper-edit-active');
        if (isChildItemActive) {
          setIsEditing(false);
        }
      }, 500);

      if (!(domNode && el instanceof Node && domNode.contains(el)) && isEditing) {
        if (isEditing) {
          sendMessageToParentWindow({
            action: 'telemetryData',
            value: telemetryData.inlineEditCTAClick(componentName, pageName, pageSlug, 'focusOut'),
            source: 'joyPreview',
          });
        }
        setIsEditing(false);
        // to show the preview button when the inline editor is closed
        sendMessageToParentWindow({ action: 'togglePreviewButton', value: { showPreviewCTA: true, showInlineEditorActionBar: false }, source: 'joyPreview' });
      }
    };
    if (isEditing) {
      withWindow(window => {
        window.document.addEventListener('mousedown', handleClickOutside);
      });
    }
    return () => {
      withWindow(window => {
        window.document.removeEventListener('mousedown', handleClickOutside);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // First match wins. Clicking the wrapper fires this action directly,
  // skipping the intermediate "click an icon in the toolbar" step. (PRNT-4176)
  const primaryActionKey = useMemo(() => PRIMARY_ACTION_KEYS.find(key => key in actionData), [actionData]);

  // Whether the section has a non-primary shortcut (Fonts / visibility) that
  // can only be reached through the toolbar. On mobile we keep the old
  // tap-to-toggle-toolbar flow only for these, since single-action sections
  // would otherwise need two taps for no benefit.
  const hasSecondaryShortcut = useMemo(() => InlineEditorActions.FONT in actionData || InlineEditorActions.VISIBILITY in actionData, [actionData]);

  const handleOnClick = useEventCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    // Mobile inline-edit has no hover gesture, so the toolbar can only appear
    // when the wrapper is tapped. For sections with a secondary shortcut
    // (Fonts) the old tap-to-toggle-toolbar flow is the only way to reach it;
    // single-action sections still fire the primary directly. (PRNT-4176)
    if (isMobile && isParentInMobileResolution && hasSecondaryShortcut) {
      setIsEditing(prev => {
        sendMessageToParentWindow({
          action: 'togglePreviewButton',
          value: { showPreviewCTA: prev, showInlineEditorActionBar: !prev },
          source: 'joyPreview',
        });
        sendMessageToParentWindow({
          action: 'telemetryData',
          value: telemetryData.inlineEditCTAClick(componentName, pageName, pageSlug, prev ? 'focusOut' : 'focusIn'),
          source: 'joyPreview',
        });
        return !prev;
      });
      return;
    }
    if (!primaryActionKey) return;
    const primaryAction = actionData[primaryActionKey];
    if (!primaryAction) return;
    sendMessageToParentWindow({
      action: 'telemetryData',
      value: telemetryData.inlineActionClicked(componentName, pageName, pageSlug),
      source: 'joyPreview',
    });
    primaryAction();
  });

  const applyScaling = useCallback((value: number) => value / scaleValue, [scaleValue]);

  const onActionClick = useEventCallback(() => {
    sendMessageToParentWindow({ action: 'telemetryData', value: telemetryData.inlineActionClicked(componentName, pageName, pageSlug), source: 'joyPreview' });
  });

  const handleOnMouseEnter = useCallback(() => {
    if (!(isMobile && isParentInMobileResolution)) {
      mouseInside.current = true;
      setIsHovered(true);
    }
  }, [isMobile, isParentInMobileResolution]);

  useEffect(() => {
    const handlePosition = () => {
      const rect = actionBarRef.current?.getBoundingClientRect();
      // Check if the element's top is out of the viewport
      if (rect?.top && rect.top < 0) {
        setPosition('bottom');
      }
    };

    // Run on mount and on window resize or scroll
    setTimeout(() => handlePosition(), 300);
  }, []);

  const actionBarTop = useMemo(() => {
    if ((isMobile && isParentInMobileResolution) || position === 'bottom') {
      return 'unset';
    } else if (wrapperType === 'actionOutside') {
      return isSticky ? '0px' : pxToRem(applyScaling(-80));
    } else if (wrapperType === 'actionInside' && actionInsideVerticalAlign === 'top') {
      return ACTION_INSIDE_EDGE_OFFSET;
    } else if (wrapperType === 'actionInside' && actionInsideVerticalAlign === 'bottom') {
      return 'unset';
    } else {
      return '50%';
    }
  }, [actionInsideVerticalAlign, applyScaling, isMobile, isParentInMobileResolution, isSticky, position, wrapperType]);

  const handleOnMouseOver = useCallback(() => {
    const domNode = wrapperRef.current;
    setTimeout(() => {
      const isChildItemActive = !!domNode?.querySelector('.inline-edit-hover-active');
      if (isChildItemActive) {
        setIsHovered(false);
      } else if (mouseInside.current) {
        setIsHovered(true);
      }
    }, 200);
  }, []);

  const generatedClassNames = useMemo(() => {
    let classNames = 'inline-editor-wrapper';
    if (isEditing) {
      classNames = `${classNames} inline-editor-wrapper-edit-active`;
    } else {
      classNames = `${classNames} inline-editor-wrapper-edit-inactive`;
    }
    if (isHovered) {
      classNames = `${classNames} inline-edit-hover-active`;
    } else {
      classNames = `${classNames} inline-edit-hover-inactive`;
    }
    return classNames;
  }, [isEditing, isHovered]);

  const insetParser = useCallback(
    (insetValue: number | { x: number; y: number }, offset: number = 0) => {
      if (typeof insetValue === 'number') {
        return { x: pxToRem(applyScaling(insetValue) + offset), y: pxToRem(applyScaling(insetValue) + offset) };
      }
      const xValue = insetValue.x ? pxToRem(applyScaling(insetValue.x) + offset) : '0px';
      const yValue = insetValue.y ? pxToRem(applyScaling(insetValue.y) + offset) : '0px';

      return { x: xValue, y: yValue };
    },
    [applyScaling],
  );

  const insetToCssStyleFormatter = useCallback((insetObj: { x: string; y: string }) => {
    return `${insetObj.y} ${insetObj.x}`;
  }, []);

  const hasHighlightState = !isEditing && isHovered && !(isMobile && isParentInMobileResolution);
  // mouseInside.current gates out the showIntro-only case (isHovered=true but no pointer)
  const showToolbarOnHover = !isEditing && isHovered && mouseInside.current && !(isMobile && isParentInMobileResolution);
  const hoverState: CSSObject = useMemo(() => {
    return {
      _after: {
        position: 'absolute',
        inset: wrapperType === 'actionOutside' ? insetToCssStyleFormatter(insetParser(inset)) : 0,
        background: wrapperType === 'actionOutside' ? 'rgba(255, 255, 255, 0.20)' : '',
      },
      _before: {
        content: `"${elementLabel}"`,
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        color: 'white',
        ...(labelPosition === 'bottom'
          ? {
              bottom: wrapperType === 'actionOutside' ? pxToRem(applyScaling(-45)) : pxToRem(applyScaling(16)),
            }
          : {
              top: wrapperType === 'actionOutside' ? pxToRem(applyScaling(-45)) : pxToRem(applyScaling(16)),
            }),
        left: wrapperType === 'actionOutside' ? insetParser(inset).x : pxToRem(applyScaling(16)),
        backgroundColor: '#4E06F2',
        fontFamily: 'Inter UI',
        fontSize: pxToRem(applyScaling(13)),
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: pxToRem(18.2),
        letterSpacing: pxToRem(-0.032),
        borderRadius: pxToRem(applyScaling(8)),
        paddingX: pxToRem(applyScaling(8)),
        height: pxToRem(applyScaling(24)),
        zIndex: 'banner',
      },
    };
  }, [applyScaling, elementLabel, inset, insetParser, insetToCssStyleFormatter, labelPosition, wrapperType]);

  const activeState: CSSObject = useMemo(() => {
    return {
      ...hoverState,
      _after: {
        background: wrapperType === 'actionOutside' ? 'rgba(255, 255, 255, 0.40)' : '',
      },
    };
  }, [hoverState, wrapperType]);

  const bottom = useMemo(() => {
    if (isMobile && isParentInMobileResolution) {
      if (isEditing) {
        return isWebsiteDesignerV3Enabled ? '20%' : pxToRem(applyScaling(24));
      }
      return '-100%';
    } else if (position === 'bottom') {
      return pxToRem(applyScaling(-80));
    } else if (wrapperType === 'actionInside' && actionInsideVerticalAlign === 'bottom') {
      return ACTION_INSIDE_EDGE_OFFSET;
    }
    return 'unset';
  }, [actionInsideVerticalAlign, applyScaling, isEditing, isMobile, isParentInMobileResolution, isWebsiteDesignerV3Enabled, position, wrapperType]);

  // For 'actionInside' (e.g. CoverPhoto's "Edit Photo" pill), keep the full
  // toolbar visible. On mobile inline-edit with a secondary shortcut, the
  // toolbar is the only way to reach it, so show the full set including the
  // primary so users can still trigger it. Otherwise (desktop or mobile
  // single-action), hide the primary — it's reachable by clicking the wrapper
  // itself — and only show secondary shortcuts. When nothing remains, suppress
  // the toolbar entirely (Page Note, GraphicAccent). (PRNT-4176)
  const actionDataForBar = useMemo<ActionData>(() => {
    if (wrapperType === 'actionInside') return actionData;
    if (isMobile && isParentInMobileResolution && hasSecondaryShortcut) return actionData;
    if (!primaryActionKey) return actionData;
    const { [primaryActionKey]: _omit, ...rest } = actionData;
    return rest as ActionData;
  }, [actionData, hasSecondaryShortcut, isMobile, isParentInMobileResolution, primaryActionKey, wrapperType]);

  const hasBarActions = Object.keys(actionDataForBar).length > 0;

  return (
    <>
      {adminDashboardInlineEditingEnabled ? (
        wrapperType === 'emptyChild' ? (
          <InlineEditorWrapper
            className={generatedClassNames}
            isPreviewing={isPreviewing}
            position="relative"
            height={8}
            __css={wrapperCSS}
          >
            <ParentMessageListener
              onScaleChange={value => setScaleValue(value)}
              onParentFocus={() => {
                if (isEditing) {
                  sendMessageToParentWindow({
                    action: 'telemetryData',
                    value: telemetryData.inlineEditCTAClick(componentName, pageName, pageSlug, 'focusOut'),
                    source: 'joyPreview',
                  });
                }
                setIsEditing(false);
              }}
            />
            <Box
              ref={childWrapperRef}
              height="100%"
              __css={containerCSS}
              onClick={handleOnClick}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={() => {
                mouseInside.current = false;
                setIsHovered(showIntro);
              }}
              onMouseOver={handleOnMouseOver}
              cursor="pointer"
              display="flex"
              alignItems="center"
            >
              <Box
                width="100%"
                position="relative"
              >
                <AnimatedBorder
                  isVisibile={isEditing || isHovered}
                  borderWidth={pxToRem(applyScaling(2))}
                  borderRadius={`${applyScaling(8)}px`}
                  inset={{ x: '0px', y: '0px' }}
                  padding={0}
                  height="1px"
                />
              </Box>

              {hasBarActions && (
                <Box
                  key={`${elementLabel}-action-bar`}
                  position={isSticky ? 'fixed' : 'absolute'}
                  width="100%"
                  zIndex="overlay"
                  top="50%"
                  left={isSticky ? (wrapperRef.current?.getBoundingClientRect().x || 0) + (wrapperRef.current?.getBoundingClientRect().width || 0) / 2 : '50%'}
                  transform={'translate(-50%, -50%)'}
                  opacity={isEditing || isHovered ? 1 : 0}
                  visibility={isEditing || isHovered ? 'visible' : 'hidden'}
                  transition=" bottom 0.5s ease-in-out, opacity 0.5s ease-in-out, visibility 0.5s ease-in-out"
                  ref={actionBarRef}
                  onClick={e => e.stopPropagation()}
                >
                  <ActionBar
                    actionData={actionDataForBar}
                    actionView={'detailed'}
                    scaleValue={scaleValue}
                    onActionClick={onActionClick}
                  />
                </Box>
              )}
            </Box>
          </InlineEditorWrapper>
        ) : (
          <InlineEditorWrapper
            className={generatedClassNames}
            isPreviewing={isPreviewing}
            position="relative"
            ref={wrapperRef}
            _after={isEditing && wrapperType === 'actionInside' ? { content: '""', inset: 0, position: 'absolute', background: 'rgba(255, 255, 255, 0.40)' } : {}}
            _hover={
              hasHighlightState && wrapperType === 'actionInside' ? { _after: { content: '""', position: 'absolute', inset: 0, background: 'rgba(255, 255, 255, 0.20)' } } : {}
            }
            _active={
              hasHighlightState && wrapperType === 'actionInside' ? { _after: { content: '""', position: 'absolute', inset: 0, background: 'rgba(255, 255, 255, 0.40)' } } : {}
            }
            __css={wrapperCSS}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={() => {
              mouseInside.current = false;
              setIsHovered(showIntro);
            }}
            onMouseOver={handleOnMouseOver}
          >
            {stickyOnScroll && (
              <Box
                id={observerMarkId}
                width="100%"
                position="absolute"
                top={pxToRem(applyScaling(-80))}
              />
            )}
            <ParentMessageListener
              onScaleChange={value => setScaleValue(value)}
              onParentFocus={() => {
                if (isEditing) {
                  sendMessageToParentWindow({
                    action: 'telemetryData',
                    value: telemetryData.inlineEditCTAClick(componentName, pageName, pageSlug, 'focusOut'),
                    source: 'joyPreview',
                  });
                }
                setIsEditing(false);
              }}
            />
            <Box
              ref={childWrapperRef}
              minHeight={minHeight || childWrapperRef.current?.firstElementChild?.clientHeight}
              __css={hasHighlightState ? { ...containerCSS, ...hoverState } : containerCSS}
              _hover={hasHighlightState ? hoverState : {}}
              _active={activeState ? activeState : {}}
              _after={{ content: '""' }}
              onClick={handleOnClick}
              cursor="pointer"
            >
              {children}
              <AnimatedBorder
                isVisibile={isEditing || isHovered}
                borderWidth={pxToRem(applyScaling(2))}
                borderRadius={`${applyScaling(8)}px`}
                inset={wrapperType === 'actionOutside' ? insetParser(inset) : { x: '0px', y: '0px' }}
                padding={wrapperType === 'actionOutside' ? 0 : pxToRem(applyScaling(8))}
              />
            </Box>
            {hasBarActions && (
              <Box
                key={`${elementLabel}-action-bar`}
                position={(isMobile && isParentInMobileResolution) || isSticky ? 'fixed' : 'absolute'}
                width="fit-content"
                zIndex="overlay"
                top={actionBarTop}
                left={isSticky ? (wrapperRef.current?.getBoundingClientRect().x || 0) + (wrapperRef.current?.getBoundingClientRect().width || 0) / 2 : '50%'}
                transform={
                  wrapperType === 'actionOutside' || (isMobile && isParentInMobileResolution) || (wrapperType === 'actionInside' && actionInsideVerticalAlign !== 'center')
                    ? 'translate(-50%)'
                    : 'translate(-50%, -50%)'
                }
                bottom={bottom}
                opacity={isEditing || showToolbarOnHover ? 1 : 0}
                visibility={isEditing || showToolbarOnHover ? 'visible' : 'hidden'}
                transition=" bottom 0.5s ease-in-out, opacity 0.5s ease-in-out, visibility 0.5s ease-in-out"
                ref={actionBarRef}
                onClick={e => e.stopPropagation()}
              >
                <ActionBar
                  actionData={actionDataForBar}
                  actionView={wrapperType === 'actionOutside' ? 'compact' : 'detailed'}
                  scaleValue={scaleValue}
                  onActionClick={onActionClick}
                />
              </Box>
            )}
          </InlineEditorWrapper>
        )
      ) : (
        <>{children}</>
      )}
    </>
  );
};

InlineEditor.displayName = 'InlineEditor';
